import { Button } from "@meiden-connect/ui";
import type React from "react";
import { useCanvasStore, type CanvasTool, type ShapeKind } from "../store/canvas-store";
import {
  PEN_COLOR_HEX,
  savePenColor,
  type PenColor,
} from "../../../lib/pen-preference";
import {
  saveStickyColor,
  STICKY_COLOR_HEX,
  type StickyColor,
} from "../../../lib/sticky-preference";

// 下部中央のフローティングツールバー。
// 図形・ペン・付箋はサブ選択パネルで種類や色を選択できる。
export function ToolBar(): JSX.Element {
  const tool = useCanvasStore((s) => s.tool);
  const setTool = useCanvasStore((s) => s.setTool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const setShapeKind = useCanvasStore((s) => s.setShapeKind);
  const penColor = useCanvasStore((s) => s.penColor);
  const setPenColor = useCanvasStore((s) => s.setPenColor);
  const stickyColor = useCanvasStore((s) => s.stickyColor);
  const setStickyColor = useCanvasStore((s) => s.setStickyColor);

  const buttons: { tool: CanvasTool; label: string; aria: string }[] = [
    { tool: "place-sticky", label: "付箋", aria: "付箋を配置" },
    { tool: "place-shape", label: "図形", aria: "図形を配置" },
    { tool: "place-text", label: "テキスト", aria: "テキストを配置" },
    { tool: "draw-pen", label: "ペン", aria: "ペンで描画" },
  ];

  const choosePenColor = (color: PenColor): void => {
    setPenColor(color);
    savePenColor(color);
  };
  const chooseStickyColor = (color: StickyColor): void => {
    setStickyColor(color);
    saveStickyColor(color);
  };

  return (
    <div className="canvas-toolbar-shell" role="group" aria-label="キャンバスツール">
      {tool === "place-shape" ? (
        <SubPanel label="図形の種類">
          {SHAPE_OPTIONS.map((option) => (
            <Button
              key={option.kind}
              type="button"
              variant={shapeKind === option.kind ? "action emphasis" : "action"}
              aria-pressed={shapeKind === option.kind}
              onClick={() => setShapeKind(option.kind)}
            >
              {option.label}
            </Button>
          ))}
        </SubPanel>
      ) : null}
      {tool === "draw-pen" ? (
        <SubPanel label="ペンの色">
          {PEN_COLOR_OPTIONS.map((color) => (
            <ColorButton
              key={color}
              label={PEN_COLOR_LABEL[color]}
              color={PEN_COLOR_HEX[color]}
              selected={penColor === color}
              onClick={() => choosePenColor(color)}
            />
          ))}
        </SubPanel>
      ) : null}
      {tool === "place-sticky" ? (
        <SubPanel label="付箋の色">
          {STICKY_COLOR_OPTIONS.map((color) => (
            <ColorButton
              key={color}
              label={STICKY_COLOR_LABEL[color]}
              color={STICKY_COLOR_HEX[color]}
              selected={stickyColor === color}
              onClick={() => chooseStickyColor(color)}
            />
          ))}
        </SubPanel>
      ) : null}
      <div role="toolbar" aria-label="キャンバスツールバー" className="canvas-toolbar">
        {buttons.map((b) => (
          <Button
            key={b.tool}
            type="button"
            aria-label={b.aria}
            aria-pressed={tool === b.tool}
            onClick={() => setTool(tool === b.tool ? "idle" : b.tool)}
            data-testid={`tool-button-${b.tool}`}
            variant={tool === b.tool ? "action emphasis" : "action"}
          >
            {b.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function SubPanel({ children, label }: { readonly children: React.ReactNode; readonly label: string }): JSX.Element {
  return (
    <div className="canvas-toolbar-subpanel" aria-label={label}>
      <span className="canvas-toolbar-subpanel-label">{label}</span>
      <div className="canvas-toolbar-subpanel-actions">{children}</div>
    </div>
  );
}

function ColorButton({
  color,
  label,
  onClick,
  selected,
}: {
  readonly color: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly selected: boolean;
}): JSX.Element {
  return (
    <Button
      type="button"
      variant={selected ? "action emphasis" : "action"}
      aria-label={label}
      aria-pressed={selected}
      onClick={onClick}
      className="canvas-color-button"
      style={{ "--canvas-color-swatch": color } as React.CSSProperties}
    >
      <span className="canvas-color-swatch" aria-hidden="true" />
      {label}
    </Button>
  );
}

const SHAPE_OPTIONS: readonly { readonly kind: ShapeKind; readonly label: string }[] = [
  { kind: "rectangle", label: "四角" },
  { kind: "circle", label: "丸" },
  { kind: "arrow", label: "矢印" },
];

const PEN_COLOR_OPTIONS: readonly PenColor[] = ["black", "red", "blue"];
const PEN_COLOR_LABEL: Record<PenColor, string> = {
  black: "黒",
  red: "赤",
  blue: "青",
};

const STICKY_COLOR_OPTIONS: readonly StickyColor[] = ["yellow", "pink", "blue", "green"];
const STICKY_COLOR_LABEL: Record<StickyColor, string> = {
  yellow: "黄",
  pink: "桃",
  blue: "青",
  green: "緑",
};
